import { useIsCWFPromptOptionsEnabled } from '@modules/common/hooks';
import { SeePromptExamplesLink } from '@modules/oracle/components/SeePromptExamplesLink';
import { useGenerateWisdomResponse } from '@modules/oracle/hooks';
import { StopCircleRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  IconButton,
  Stack,
  StackProps,
  TextField,
  TextFieldProps,
  Tooltip,
  useControlled,
} from '@mui/material';
import {
  FocusEventHandler,
  forwardRef,
  useCallback,
  useImperativeHandle,
} from 'react';

export interface AskOracleInputActionsHandle {
  submit: () => void;
}

interface AskOracleInputProps {
  value?: string;
  onChange?: TextFieldProps['onChange'];
  shouldBlockSubmit?: boolean;
  editedFromResponseID?: string;
  components?: { TextField: TextFieldProps };
  onSubmit?: (queryText: string) => void;
  shouldShowStopButton?: boolean;
  onStop?: () => void;
  containerSxProps?: StackProps['sx'];
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

export const AskOracleInput = forwardRef<
  AskOracleInputActionsHandle,
  AskOracleInputProps
>(
  (
    {
      value,
      editedFromResponseID,
      shouldBlockSubmit = false,
      components,
      onChange,
      onSubmit: onSubmitProp,
      shouldShowStopButton = false,
      onStop: onStopProp,
      containerSxProps,
      onFocus,
    },
    forwardedRef,
  ) => {
    const isCWFPromptOptionsEnabled = useIsCWFPromptOptionsEnabled();

    const [question, setQuestion] = useControlled({
      controlled: value,
      default: '',
      name: 'AskOracleInput',
      state: 'question',
    });

    const [generateWisdomResponse, { isLoading }] = useGenerateWisdomResponse({
      editedFromResponseID,
    });

    const onSubmit = useCallback(async () => {
      if (shouldBlockSubmit) {
        return;
      }

      if (!question.trim()) {
        return;
      }

      await generateWisdomResponse({
        onGenerate: () => {
          setQuestion('');
          onSubmitProp?.(question);
        },
        question,
      });
    }, [
      generateWisdomResponse,
      onSubmitProp,
      question,
      setQuestion,
      shouldBlockSubmit,
    ]);

    useImperativeHandle(
      forwardedRef,
      () => {
        return {
          submit: onSubmit,
        };
      },
      [onSubmit],
    );

    return (
      <Stack
        component={'form'}
        sx={{ ...containerSxProps, width: '100%' }}
        alignItems="center"
        onSubmit={(eve) => {
          eve.preventDefault();
          onSubmit();
        }}
      >
        <TextField
          fullWidth
          multiline
          autoFocus
          value={question}
          maxRows={6}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck="false"
          label=""
          disabled={isLoading}
          placeholder={
            shouldBlockSubmit
              ? 'Stop generating the response to ask a new question'
              : 'Ask Wisdom'
          }
          variant="outlined"
          sx={{
            maxWidth: '1024px',
          }}
          onFocus={onFocus}
          onKeyDown={(event) => {
            if (shouldBlockSubmit) {
              return;
            }

            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              onSubmit();
            }
          }}
          helperText={
            <Stack
              direction="row"
              justifyContent={
                isCWFPromptOptionsEnabled ? 'space-between' : 'center'
              }
            >
              <Box>
                Wisdom strives for accuracy, but it's always learning. Your
                queries help it grow smarter!
              </Box>
              <SeePromptExamplesLink />
            </Stack>
          }
          InputProps={{
            endAdornment: shouldShowStopButton ? (
              <Tooltip arrow title="Stop response" placement="top">
                <IconButton
                  size="small"
                  onClick={onStopProp}
                  type="button"
                  color="primary"
                >
                  <StopCircleRounded />
                </IconButton>
              </Tooltip>
            ) : (
              <LoadingButton
                variant="contained"
                size="small"
                disabled={shouldBlockSubmit || !question}
                type="submit"
                loading={isLoading}
              >
                Go
              </LoadingButton>
            ),
          }}
          {...components?.TextField}
          onChange={(e) => {
            setQuestion(e.target.value);
            onChange?.(e);
          }}
        />
      </Stack>
    );
  },
);
